/**
 * TagListPage - Tag management page with list, search, and merge functionality
 *
 * Displays tags with search, create, edit, and merge capabilities
 */
package com.castly.admin.ui.tags

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.components.MergeDialog
import com.castly.admin.ui.components.MergeEntity
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.admin.ui.components.IconButton
import com.castly.admin.ui.components.Tooltip
import com.castly.admin.ui.icons.*
import com.castly.shared.dto.TagDto
import com.castly.shared.dto.TagWithCountDto
import com.castly.shared.dto.CreateTagRequest
import com.castly.shared.dto.UpdateTagRequest
import com.castly.shared.dto.MergeTagRequest
import com.castly.shared.dto.PodcastDto
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun TagListPage(apiClient: ApiClient) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")
    val canDelete = userRoleStr.uppercase() in setOf("ADMIN", "SUPER_ADMIN")

    var tags by remember { mutableStateOf<List<TagWithCountDto>>(emptyList()) }
    var allTags by remember { mutableStateOf<List<TagWithCountDto>>(emptyList()) }
    var filteredTags by remember { mutableStateOf<List<TagWithCountDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var showCreateDialog by remember { mutableStateOf(false) }
    var showEditDialog by remember { mutableStateOf(false) }
    var showMergeDialog by remember { mutableStateOf(false) }
    var selectedTag by remember { mutableStateOf<TagWithCountDto?>(null) }
    var expandedTagId by remember { mutableStateOf<String?>(null) }
    var expandedItems by remember { mutableStateOf<List<com.castly.shared.dto.AudiobookDto>>(emptyList()) }
    var expandedPodcasts by remember { mutableStateOf<List<com.castly.shared.dto.PodcastDto>>(emptyList()) }
    var expandedLoading by remember { mutableStateOf(false) }
    var isCreating by remember { mutableStateOf(false) }
    var isUpdating by remember { mutableStateOf(false) }
    var isMerging by remember { mutableStateOf(false) }
    var createError by remember { mutableStateOf<String?>(null) }
    var updateError by remember { mutableStateOf<String?>(null) }
    var mergeError by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    fun loadTags() {
        scope.launch {
            loading = true
            error = null
            try {
                val result = apiClient.listTags(q = searchQuery.takeIf { it.isNotBlank() })
                tags = result
                allTags = result
                filteredTags = result
            } catch (e: Exception) {
                error = e.message ?: "Failed to load tags"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(searchQuery) {
        loadTags()
    }

    Div({
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color(colors.surfaceHover))
            themeTransition()
        }
    }) {
        // Header with search and create button
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.lg)
                property("flex-shrink", "0")
                themeTransition()
            }
        }) {
            // Search bar
            Div({
                style {
                    flex("1")
                    maxWidth(400.px)
                }
            }) {
                Input(InputType.Text) {
                    value(searchQuery)
                    onInput { searchQuery = it.value }
                    attr("placeholder", "Search tags...")
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.lg)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                    }
                }
            }

            // Create button
            if (canEdit) {
                Tooltip(text = "Create Tag") {
                    Button({
                        onClick { showCreateDialog = true; createError = null }
                        style {
                            padding(Spacing.sm)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            width(36.px)
                            height(36.px)
                        }
                    }) {
                        IconPlus(size = 18)
                    }
                }
            }
        }

        // Main content area
        Div({
            style {
                flex("1")
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            when {
                loading -> {
                    Div({
                        style {
                            textAlign("center")
                            padding(32.px)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Text("Loading tags...")
                    }
                }

                error != null -> {
                    Div({
                        style {
                            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                            border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                            borderRadius(BorderRadius.md)
                            padding(Spacing.lg)
                            themeTransition()
                        }
                    }) {
                        Text("Error: $error")
                    }
                }

                filteredTags.isEmpty() -> {
                    Div({
                        style {
                            textAlign("center")
                            padding(48.px, Spacing.xxl)
                            backgroundColor(Color(colors.surface))
                            borderRadius(BorderRadius.lg)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            themeTransition()
                        }
                    }) {
                        H3({
                            style {
                                margin(0.px)
                                marginBottom(Spacing.sm)
                                fontSize(Typography.xl)
                                fontWeight("600")
                                color(Color(colors.text))
                            }
                        }) {
                            Text(if (searchQuery.isNotBlank()) "No tags found" else "No tags yet")
                        }
                        P({
                            style {
                                margin(0.px)
                                fontSize(Typography.base)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            Text(if (searchQuery.isNotBlank()) "Try adjusting your search query" else "Create your first tag to get started")
                        }
                    }
                }

                else -> {
                    // Tags list
                    Div({
                        style {
                            backgroundColor(Color(colors.surface))
                            borderRadius(BorderRadius.lg)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            overflow("hidden")
                            themeTransition()
                        }
                    }) {
                        filteredTags.forEachIndexed { index, tag ->
                            val isExpanded = expandedTagId == tag.id
                            Div({
                                style {
                                    if (index > 0) property("border-top", "1px solid ${colors.border}")
                                }
                            }) {
                                // Tag row (clickable to expand)
                                Div({
                                    onClick {
                                        if (expandedTagId == tag.id) {
                                            expandedTagId = null
                                        } else {
                                            expandedTagId = tag.id
                                            expandedLoading = true
                                            scope.launch {
                                                try {
                                                    expandedItems = apiClient.listAudiobooks(query = null, authorId = null, readerId = null).audiobooks.filter { ab ->
                                                        ab.tags?.contains(tag.name) == true
                                                    }
                                                    expandedPodcasts = apiClient.listPodcasts(tags = listOf(tag.id)).items
                                                } catch (_: Exception) {
                                                    expandedItems = emptyList()
                                                    expandedPodcasts = emptyList()
                                                } finally {
                                                    expandedLoading = false
                                                }
                                            }
                                        }
                                    }
                                    style {
                                        padding(Spacing.lg)
                                        cursor("pointer")
                                        property("transition", "background-color 0.2s")
                                        if (isExpanded) backgroundColor(Color(colors.surfaceHover))
                                    }
                                    onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                                    onMouseOut { if (!isExpanded) it.target.asDynamic().style.backgroundColor = "transparent" }
                                }) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            justifyContent(JustifyContent.SpaceBetween)
                                            alignItems(AlignItems.Center)
                                        }
                                    }) {
                                        Div({
                                            style {
                                                display(DisplayStyle.Flex)
                                                alignItems(AlignItems.Center)
                                                gap(Spacing.sm)
                                                flex("1")
                                            }
                                        }) {
                                            // Expand indicator
                                            Span({ style { fontSize(Typography.base); color(Color(colors.textMuted)) } }) {
                                                Text(if (isExpanded) "▼" else "▶")
                                            }
                                            Div {
                                                // Name
                                                Div({
                                                    style {
                                                        fontSize(Typography.lg)
                                                        fontWeight("600")
                                                        color(Color(colors.text))
                                                        marginBottom(Spacing.xs)
                                                    }
                                                }) {
                                                    Text(tag.name)
                                                }

                                                // Usage count
                                                Div({
                                                    style {
                                                        fontSize(13.px)
                                                        color(Color(colors.textMuted))
                                                    }
                                                }) {
                                                    val totalCount = tag.podcastCount + tag.audiobookCount
                                                    Text("Used in $totalCount items (${tag.podcastCount} podcasts, ${tag.audiobookCount} audiobooks)")
                                                }
                                            }
                                        }

                                        // Action buttons
                                        if (canEdit || canDelete) {
                                            Div({
                                                style {
                                                    display(DisplayStyle.Flex)
                                                    gap(Spacing.xs)
                                                }
                                                onClick { it.stopPropagation() }
                                            }) {
                                                if (canDelete) {
                                                    IconButton(
                                                        icon = { IconRefreshCw(size = 16) },
                                                        label = "Merge",
                                                        onClick = {
                                                            selectedTag = tag
                                                            showMergeDialog = true
                                                            mergeError = null
                                                        }
                                                    )
                                                }

                                                if (canEdit) {
                                                    IconButton(
                                                        icon = { IconEdit(size = 16) },
                                                        label = "Edit",
                                                        onClick = {
                                                            selectedTag = tag
                                                            showEditDialog = true
                                                            updateError = null
                                                        }
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }

                                // Expanded items panel
                                if (isExpanded) {
                                    Div({
                                        style {
                                            padding(0.px, Spacing.lg, Spacing.lg, Spacing.lg)
                                            backgroundColor(Color(colors.surfaceHover))
                                            maxHeight(300.px)
                                            overflow("auto")
                                        }
                                    }) {
                                        if (expandedLoading) {
                                            Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); textAlign("center") } }) {
                                                Text("Loading items...")
                                            }
                                        } else if (expandedPodcasts.isEmpty() && expandedItems.isEmpty()) {
                                            Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); textAlign("center") } }) {
                                                Text("No items found for this tag")
                                            }
                                        } else {
                                            // Podcasts
                                            expandedPodcasts.forEach { podcast ->
                                                Div({
                                                    onClick { it.stopPropagation() }
                                                    style {
                                                        padding(Spacing.sm, Spacing.md)
                                                        backgroundColor(Color(colors.surface))
                                                        borderRadius(BorderRadius.sm)
                                                        marginBottom(Spacing.xs)
                                                        cursor("pointer")
                                                        display(DisplayStyle.Flex)
                                                        alignItems(AlignItems.Center)
                                                        gap(Spacing.sm)
                                                    }
                                                    onMouseOver { it.target.asDynamic().style.opacity = "0.8" }
                                                    onMouseOut { it.target.asDynamic().style.opacity = "1" }
                                                }) {
                                                    Span({ style { fontSize(Typography.xs); padding(2.px, 6.px); backgroundColor(Color("#2196F3")); color(Color("#fff")); borderRadius(BorderRadius.sm) } }) { Text("Podcast") }
                                                    Span({ style { fontWeight("500"); color(Color(colors.text)) } }) { Text(podcast.title) }
                                                }
                                            }
                                            // Audiobooks
                                            expandedItems.forEach { audiobook ->
                                                Div({
                                                    onClick { it.stopPropagation() }
                                                    style {
                                                        padding(Spacing.sm, Spacing.md)
                                                        backgroundColor(Color(colors.surface))
                                                        borderRadius(BorderRadius.sm)
                                                        marginBottom(Spacing.xs)
                                                        cursor("pointer")
                                                        display(DisplayStyle.Flex)
                                                        alignItems(AlignItems.Center)
                                                        gap(Spacing.sm)
                                                    }
                                                    onMouseOver { it.target.asDynamic().style.opacity = "0.8" }
                                                    onMouseOut { it.target.asDynamic().style.opacity = "1" }
                                                }) {
                                                    Span({ style { fontSize(Typography.xs); padding(2.px, 6.px); backgroundColor(Color("#4CAF50")); color(Color("#fff")); borderRadius(BorderRadius.sm) } }) { Text("Audiobook") }
                                                    Span({ style { fontWeight("500"); color(Color(colors.text)) } }) { Text(audiobook.title) }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Create tag dialog
    if (showCreateDialog) {
        TagFormDialog(
            tag = null,
            isLoading = isCreating,
            errorMessage = createError,
            onSave = { name ->
                scope.launch {
                    isCreating = true
                    createError = null
                    try {
                        apiClient.createTag(CreateTagRequest(name))
                        showCreateDialog = false
                        loadTags()
                    } catch (e: Exception) {
                        createError = e.message ?: "Failed to create tag"
                    } finally {
                        isCreating = false
                    }
                }
            },
            onCancel = {
                if (!isCreating) {
                    showCreateDialog = false
                    createError = null
                }
            }
        )
    }

    // Edit tag dialog
    selectedTag?.let { tag ->
        if (showEditDialog) {
            TagFormDialog(
                tag = tag,
                isLoading = isUpdating,
                errorMessage = updateError,
                onSave = { name ->
                    scope.launch {
                        isUpdating = true
                        updateError = null
                        try {
                            apiClient.updateTag(tag.id, UpdateTagRequest(name))
                            showEditDialog = false
                            selectedTag = null
                            loadTags()
                        } catch (e: Exception) {
                            updateError = e.message ?: "Failed to update tag"
                        } finally {
                            isUpdating = false
                        }
                    }
                },
                onCancel = {
                    if (!isUpdating) {
                        showEditDialog = false
                        selectedTag = null
                        updateError = null
                    }
                }
            )
        }
    }

    // Merge dialog
    selectedTag?.let { tag ->
        if (showMergeDialog) {
            MergeDialog(
                entityType = "Tag",
                sourceEntity = MergeEntity(
                    id = tag.id.toIntOrNull() ?: 0,
                    name = tag.name,
                    count = tag.podcastCount + tag.audiobookCount
                ),
                targetEntities = allTags
                    .filter { it.id != tag.id }
                    .map { MergeEntity(it.id.toIntOrNull() ?: 0, it.name, it.podcastCount + it.audiobookCount) },
                isLoading = isMerging,
                errorMessage = mergeError,
                onConfirm = { targetId ->
                    scope.launch {
                        isMerging = true
                        mergeError = null
                        try {
                            apiClient.mergeTags(MergeTagRequest(tag.id, targetId.toString()))
                            showMergeDialog = false
                            selectedTag = null
                            loadTags()
                        } catch (e: Exception) {
                            mergeError = e.message ?: "Failed to merge tags"
                        } finally {
                            isMerging = false
                        }
                    }
                },
                onCancel = {
                    if (!isMerging) {
                        showMergeDialog = false
                        selectedTag = null
                        mergeError = null
                    }
                }
            )
        }
    }
}

@Composable
private fun TagFormDialog(
    tag: TagWithCountDto?,
    isLoading: Boolean,
    errorMessage: String?,
    onSave: (String) -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    var name by remember(tag) { mutableStateOf(tag?.name ?: "") }
    val isValid = name.isNotBlank()
    val isEditMode = tag != null

    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
        }
        onClick { if (!isLoading) onCancel() }
    }) {
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                padding(Spacing.xxl)
                maxWidth(400.px)
                width(90.percent)
                themeTransition()
            }
            onClick { it.stopPropagation() }
        }) {
            H2({
                style {
                    margin(0.px)
                    marginBottom(Spacing.lg)
                    fontSize(Typography.xl)
                    fontWeight("600")
                    color(Color(colors.text))
                }
            }) {
                Text(if (isEditMode) "Edit Tag" else "Create Tag")
            }

            // Error message
            errorMessage?.let { error ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                        themeTransition()
                    }
                }) {
                    Text(error)
                }
            }

            // Name field
            Div({
                style {
                    marginBottom(Spacing.xl)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.text))
                        marginBottom(6.px)
                    }
                }) {
                    Text("Name")
                    Span({
                        style {
                            color(Color(colors.danger))
                            marginLeft(2.px)
                        }
                    }) {
                        Text("*")
                    }
                }

                Input(InputType.Text) {
                    value(name)
                    onInput { name = it.value }
                    attr("placeholder", "Enter tag name")
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                    justifyContent(JustifyContent.FlexEnd)
                }
            }) {
                Button({
                    onClick { if (!isLoading) onCancel() }
                    if (isLoading) disabled()
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.text))
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor(if (isLoading) "not-allowed" else "pointer")
                    }
                }) {
                    Text("Cancel")
                }

                Button({
                    onClick {
                        if (isValid && !isLoading) {
                            onSave(name)
                        }
                    }
                    if (!isValid || isLoading) disabled()
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.surface))
                        backgroundColor(if (!isValid || isLoading) Color(colors.textMuted) else Color(colors.primary))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor(if (!isValid || isLoading) "not-allowed" else "pointer")
                    }
                }) {
                    Text(when {
                        isLoading -> "Saving..."
                        isEditMode -> "Update Tag"
                        else -> "Create Tag"
                    })
                }
            }
        }
    }
}
